// Apolo IDE — radar_query: the authenticated agent→Radar READ bridge (radar-query sprint).
//
// Sibling of radar_emit (the write bridge). radar_emit let agents WRITE; this
// lets them READ the Radar state they need to act autonomously on a SCHEDULE:
//   - RadarGit/RadarSocial read their own watchlist (which sources to scan) —
//     RadarSocial especially needs the 16 IG/TikTok/Skool handles + their ids.
//   - Curador reads the new findings (status=new) it must dedupe/score/lens.
//   - The Apolo-improver curator reads its own PROPOSALS (what='proposals',
//     apolo-improver E2) — the is_proposal=1 rows it already emitted — so a run
//     doesn't re-propose what is already on the Runs-tab board (filter by
//     installState to skip the ones already installing/installed/dismissed).
// Without it the scheduled task is a static string with no live state, so a run
// only worked when the target was hard-coded into the task. Like radar_emit it
// runs in the RENDERER (radarApi carries the user's JWT) and returns a STRING
// for the model; never throws. Read-only ⇒ `mutating: false` (no approval gate).
//
// Output is compact JSON the small local model can parse. Lists are capped so a
// big feed can't blow the context window (the agent paginates by status/score).

import { radarApi as realRadarApi } from '../../radar/radarApiClient.js';

const MAX_ITEMS = 40;

export const RADAR_QUERY_DEF = {
  type: 'function',
  function: {
    name: 'radar_query',
    description:
      'Lê o estado do Radar (autenticado como o usuário — use isto pra DESCOBRIR o que processar; nunca curl/GET manual, você não tem token no shell). ' +
      'what="sources": a watchlist de fontes (filtre por kind: github_repo|github_search|instagram|tiktok|skool). ' +
      'what="findings": os achados (filtre por status: new|saved|tasked|dispatched|dismissed e/ou score: S|A|B|C). ' +
      'what="quarantine": os itens em quarentena. ' +
      'what="proposals": as propostas de melhoria do Apolo (filtre por installState: proposed|scanning|installing|installed|dismissed|failed). ' +
      'Retorna JSON compacto (até 40 itens). ' +
      'Ex: {"what":"sources","kind":"instagram"} ou {"what":"findings","status":"new"} ou {"what":"proposals","installState":"proposed"}',
    parameters: {
      type: 'object',
      properties: {
        what: { type: 'string', enum: ['sources', 'findings', 'quarantine', 'proposals'] },
        kind: { type: 'string', description: 'sources: github_repo|github_search|instagram|tiktok|skool' },
        status: { type: 'string', description: 'findings: new|saved|tasked|dispatched|dismissed' },
        score: { type: 'string', description: 'findings: S|A|B|C' },
        installState: { type: 'string', description: 'proposals: proposed|scanning|installing|installed|dismissed|failed' },
      },
      required: ['what'],
    },
  },
};

// Project each row to the few fields an agent needs — keeps the JSON small.
function slimSource(s) {
  return { id: s.id, kind: s.kind, handle: s.handle, url: s.url, lens: s.lens, cadence: s.cadence, health: s.health, lastScannedAt: s.lastScannedAt };
}
function slimFinding(f) {
  return { id: f.id, title: f.title, url: f.url, summary: f.summary, score: f.score, status: f.status, sourceId: f.sourceId, tags: f.tags };
}
function slimQuarantine(q) {
  return { id: q.id, skillName: q.skillName, repoUrl: q.repoUrl, state: q.state, findingId: q.findingId };
}
// A proposal is a finding flagged is_proposal=1 — project the finding fields the
// agent needs PLUS the install metadata so it can tell what is already on the
// board (and in what install state) before emitting a duplicate.
function slimProposal(p) {
  return {
    id: p.id,
    title: p.title,
    url: p.url,
    summary: p.summary,
    score: p.score,
    proposalSummary: p.proposalSummary,
    installKind: p.installKind,
    installTarget: p.installTarget,
    installState: p.installState,
    tags: p.tags,
  };
}

/**
 * Build the radar_query extra-tool for the agentLoop seam.
 * @param {{ api?: object }} deps  injectable radarApi for tests
 * @returns {{ def: object, execute: (args: object) => Promise<string>, mutating: false }}
 */
export function makeRadarQueryTool({ api = realRadarApi } = {}) {
  async function execute(args = {}) {
    const what = String(args.what || '').trim();
    try {
      if (what === 'sources') {
        let sources = await api.listSources();
        if (args.kind) sources = sources.filter((s) => s.kind === args.kind);
        const total = sources.length;
        const items = sources.slice(0, MAX_ITEMS).map(slimSource);
        return JSON.stringify({ what: 'sources', total, shown: items.length, items });
      }
      if (what === 'findings') {
        const params = {};
        if (args.status) params.status = args.status;
        if (args.score) params.score = args.score;
        const findings = await api.listFindings(params);
        const total = findings.length;
        const items = findings.slice(0, MAX_ITEMS).map(slimFinding);
        return JSON.stringify({ what: 'findings', total, shown: items.length, items });
      }
      if (what === 'quarantine') {
        const q = await api.listQuarantine();
        const total = q.length;
        const items = q.slice(0, MAX_ITEMS).map(slimQuarantine);
        return JSON.stringify({ what: 'quarantine', total, shown: items.length, items });
      }
      if (what === 'proposals') {
        const params = {};
        if (args.installState) params.installState = args.installState;
        const proposals = await api.listProposals(params);
        const total = proposals.length;
        const items = proposals.slice(0, MAX_ITEMS).map(slimProposal);
        return JSON.stringify({ what: 'proposals', total, shown: items.length, items });
      }
      return 'Erro radar_query: what deve ser sources, findings, quarantine ou proposals.';
    } catch (e) {
      return `Erro radar_query: ${e?.message || 'falha na API do Radar'}`;
    }
  }

  return { def: RADAR_QUERY_DEF, execute, mutating: false };
}
